import { For, createMemo, type Component } from 'solid-js';
import type { PlacedBet, WheelStyle } from '~/domain/types';
import { analyseSpread } from '~/domain/spread';
import { getWheel } from '~/domain/wheels';
import { annularSector, pocketAngle, pocketStep } from '~/view/geometry';
import { chipTier, type ChipTier } from '~/view/pocketState';

/**
 * A tiny non-interactive wheel showing a spread's coverage, for library list
 * rows (SPEC.md §5). Pockets are drawn as ring segments in physical order,
 * reusing the same geometry helpers as the full `Wheel` — this is a miniature
 * of it, not a second wheel layout. No numerals, no net bars, no gap arcs, no
 * interactivity: purely decorative, so it is `aria-hidden`.
 */

export interface WheelThumbnailProps {
  bets: readonly PlacedBet[];
  wheelStyle: WheelStyle;
  /** Rendered size in px. */
  size?: number;
  class?: string;
}

const VIEW = 40;
const CX = VIEW / 2;
const CY = VIEW / 2;
const R_INNER = 12;
const R_OUTER = 19;

/** Opacity for a covered pocket, stepped by its chip-weight tier against the median. */
const COVERED_OPACITY: Readonly<Record<ChipTier, number>> = {
  light: 0.3,
  mid: 0.55,
  heavy: 0.85,
};

/** Uncovered pockets stay low-opacity so the ring still reads as a wheel, without
 *  competing with covered pockets — deliberately below the lightest covered tier. */
const BASE_OPACITY = 0.22;

interface Segment {
  readonly path: string;
  readonly fill: string;
  readonly opacity: number;
}

const WheelThumbnail: Component<WheelThumbnailProps> = (props) => {
  const wheel = createMemo(() => getWheel(props.wheelStyle));
  const analysis = createMemo(() => analyseSpread(props.bets, props.wheelStyle));

  const baseColour = (colour: 'red' | 'black' | 'green'): string =>
    colour === 'red'
      ? 'var(--color-pocket-red)'
      : colour === 'black'
        ? 'var(--color-pocket-black)'
        : 'var(--color-pocket-green)';

  const segments = createMemo<Segment[]>(() => {
    const w = wheel();
    const a = analysis();
    const step = pocketStep(w.pocketCount);

    return w.ordered.map((pocket) => {
      const chips = a.coverage.get(pocket.id)?.chips ?? 0;
      const angle = pocketAngle(pocket.ringIndex, w.pocketCount);
      const path = annularSector(CX, CY, R_INNER, R_OUTER, angle - step / 2, angle + step / 2);

      if (chips > 0) {
        return { path, fill: 'var(--color-placed)', opacity: COVERED_OPACITY[chipTier(chips, a.medianChips)] };
      }
      return { path, fill: baseColour(pocket.colour), opacity: BASE_OPACITY };
    });
  });

  return (
    <svg
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      width={props.size ?? 34}
      height={props.size ?? 34}
      aria-hidden="true"
      class={props.class}
      style={{ 'pointer-events': 'none' }}
    >
      {/* Backdrop so the low-opacity fills read against near-black, not white. */}
      <circle cx={CX} cy={CY} r={R_OUTER} fill="var(--color-surface-0)" />
      <For each={segments()}>
        {(segment) => (
          <path
            d={segment.path}
            fill={segment.fill}
            opacity={segment.opacity}
            stroke="var(--color-surface-0)"
            stroke-width="0.5"
          />
        )}
      </For>
    </svg>
  );
};

export default WheelThumbnail;
